import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, FileText } from "lucide-react";

export const usd = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n || 0));

export default function InvoiceModal({ open, onOpenChange, county, onCreated }) {
  const year = new Date().getFullYear();
  const [period, setPeriod] = useState(`${year} Annual Contract`);
  const [items, setItems] = useState([]);
  const [due, setDue] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (county && open) {
      setPeriod(`${year} Annual Contract`);
      setItems([{ description: `Dual Care Platform — ${year} Annual Contract`, amount: county.annual_contract_amount || 0 }]);
      setDue("");
      setNotes("");
    }
  }, [county, open]);

  if (!county) return null;

  const updateItem = (i, k, v) => {
    const next = [...items];
    next[i] = { ...next[i], [k]: k === "amount" ? v : v };
    setItems(next);
  };
  const addItem = () => setItems([...items, { description: "", amount: 0 }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const subtotal = items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/invoices", {
        county_id: county.id,
        period_label: period,
        line_items: items.map((it) => ({ description: it.description, amount: parseFloat(it.amount) || 0 })),
        due_date: due || null,
        notes,
      });
      toast.success("Invoice created");
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const u = county.usage || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="invoice-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <FileText className="h-5 w-5 text-[#0d9488]" /> Create Invoice — {county.name}
          </DialogTitle>
          <DialogDescription>Yearly contract billing for platform use.</DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
          <span className="font-semibold text-slate-700">Usage this contract:</span> {u.parents ?? 0} parents · {u.completed_visits ?? 0} completed visits · {u.reports ?? 0} reports
        </div>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Billing period</Label>
            <Input data-testid="invoice-period-input" value={period} onChange={(e) => setPeriod(e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label>Line items</Label>
            {items.map((it, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  data-testid={`invoice-item-desc-${i}`}
                  className="flex-1"
                  value={it.description}
                  onChange={(e) => updateItem(i, "description", e.target.value)}
                  placeholder="Description"
                />
                <Input
                  data-testid={`invoice-item-amount-${i}`}
                  type="number"
                  step="0.01"
                  className="w-28"
                  value={it.amount}
                  onChange={(e) => updateItem(i, "amount", e.target.value)}
                  placeholder="0.00"
                />
                {items.length > 1 && (
                  <button type="button" onClick={() => removeItem(i)} className="text-slate-400 hover:text-red-500" data-testid={`remove-item-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-line-item-btn">
              <Plus className="mr-1.5 h-4 w-4" /> Add line item
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Due date</Label>
              <Input type="date" data-testid="invoice-due-input" value={due} onChange={(e) => setDue(e.target.value)} />
            </div>
            <div className="flex items-end justify-end">
              <div className="text-right">
                <p className="text-xs uppercase tracking-wide text-slate-500">Total</p>
                <p className="font-display text-2xl font-bold text-[#1e3a8a]" data-testid="invoice-total">{usd(subtotal)}</p>
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Notes (optional)</Label>
            <Textarea data-testid="invoice-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Contract reference, PO number..." />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="create-invoice-btn" onClick={submit} disabled={loading} className="bg-[#1e3a8a] hover:bg-[#172554]">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create Invoice
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
